import { isEqualStringNoSensitive } from "@api";
import {
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Stack,
  type StackProps,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Authorizer } from "@styleguide";
import type React from "react";
import { Fragment, type SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { EnterpriseLink } from "../../ContactCreationError/styles";
import { type IEnterpriseBody, useModalOfferContext } from "../context";
import { useAutocompleteEnterprise } from "../logics/entreprise";

interface IEnterpriseInputProp extends StackProps {
  isDisabled?: boolean;
}

const AddEnterpriseSwitch = ({ isSharedEnterprise, setIsSharedEnterprise, disabled }) => (
  <Stack
    direction="row"
    spacing={3}
    sx={{
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <FormControlLabel
      control={
        <Switch
          id="switch-offer-isPermanent"
          name="switch-offer-isPermanent"
          checked={isSharedEnterprise}
          size="small"
          onClick={() => setIsSharedEnterprise(!isSharedEnterprise)}
          disabled={disabled}
          color="primary"
        />
      }
      label={
        <Typography variant="body1SemiBold">
          Voulez-vous ajouter cette entreprise à la base de données Horizon ? Une nouvelle page entreprise sera créée
          après l’ajout de cette candidature.
        </Typography>
      }
      labelPlacement="start"
    />
  </Stack>
);

export const EnterpriseInput = ({ isDisabled, ...props }: IEnterpriseInputProp) => {
  const {
    contact,
    setContact,
    enterprise,
    setEnterprise,
    isSharedEnterprise,
    setIsSharedEnterprise,
    errors,
    setErrors,
    disabled,
    applyFollowUp,
    apply,
    createOffer,
  } = useModalOfferContext();
  const [inputValue, setInputValue] = useState(enterprise?.name ?? "");
  const { enterprises, loading, loadOnType } = useAutocompleteEnterprise();

  useEffect(() => {
    if (enterprise?.name && enterprise.name !== inputValue) {
      setInputValue(enterprise.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterprise?.name]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, newEnterprise: IEnterpriseBody | null) => {
      setEnterprise(newEnterprise);
      if (newEnterprise) {
        setInputValue(newEnterprise.name);
        setErrors((errors) => ({ ...errors, enterprise: false }));
      } else {
        setInputValue("");
      }
      if (contact?.isTemp === false) {
        setContact((c) => {
          if (c) {
            return { ...c, isTemp: true };
          }
          return c;
        });
      }
    },
    [setEnterprise, setErrors, contact?.isTemp]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleInputChange = useCallback(
    (_: SyntheticEvent<Element, Event>, value: string) => {
      setInputValue(value);
      setIsSharedEnterprise(false);
      if (contact?.isTemp === false) {
        setContact((c) => {
          if (c) {
            return { ...c, isTemp: true };
          }
          return c;
        });
      }
      loadOnType(value);
    },
    [setInputValue, loadOnType, contact?.isTemp]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const options = useMemo(() => {
    const data: IEnterpriseBody[] = [];

    if (inputValue) {
      if (!loading) {
        data.push(...enterprises);
      }

      if (enterprise && enterprise._id !== "" && !data.find((e) => e._id === enterprise?._id)) {
        data.unshift(enterprise);
      }
      // push add item if the inputvalue not equal any item enterprises
      if (!loading && !data.some((e) => isEqualStringNoSensitive(e.name, inputValue))) {
        data.push({
          _id: "",
          name: inputValue,
          contacts: [],
        });
      }
    }
    return data;
  }, [enterprises, loading]);

  return (
    <Stack {...props} spacing={1}>
      <Autocomplete
        id="offers-dashboard-filters-createdBy"
        value={enterprise?.name ? enterprise : null}
        options={options}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        filterOptions={(x) => x}
        loading={loading}
        noOptionsText="Pas de résultat"
        isOptionEqualToValue={(option, value) => option?.name === value?.name}
        getOptionLabel={(enterprise) => enterprise.name || ""}
        getOptionDisabled={(enterprise) => enterprise._id === "noresult" || enterprise._id === "loading"}
        loadingText={<CircularProgress color="inherit" size="15px" />}
        renderOption={(props, enterprise) => (
          <Fragment key={enterprise?._id ? enterprise._id : "create-new-enterprise-with-current-text"}>
            {enterprise?._id && <li {...props}>{enterprise.name}</li>}

            {!loading && !enterprise?._id && (
              <Typography variant="body1SemiBold" color="primary" component={"li"} {...props}>
                + Renseigner une nouvelle entreprise
              </Typography>
            )}
          </Fragment>
        )}
        disabled={disabled ?? isDisabled}
        renderInput={(params) => {
          // biome-ignore lint/complexity/noUselessFragments: <explanation>
          params.InputProps.endAdornment = <>{params.InputProps.endAdornment}</>;
          return (
            <TextField
              {...params}
              label="Entreprise"
              required={!applyFollowUp || createOffer || apply.status === "success"}
              size="small"
              error={!!errors.enterprise}
              disabled={disabled ?? isDisabled}
              fullWidth
            />
          );
        }}
      />
      {enterprise?._id && !isDisabled && (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
          }}
        >
          <Typography variant="body1">Voir la page de l’entreprise&nbsp;</Typography>
          &ldquo;
          <Typography variant="body1SemiBold">
            <EnterpriseLink target="_blank" to={`${"/v2/enterprise"}/${enterprise._id}`}>
              {enterprise.name}
            </EnterpriseLink>
          </Typography>
          &rdquo;
        </Stack>
      )}
      {!enterprise?._id && enterprise?.name && (
        <Authorizer requiredPermissions={["offers.dashboard.enterprise.switch"]}>
          <AddEnterpriseSwitch
            isSharedEnterprise={isSharedEnterprise}
            setIsSharedEnterprise={setIsSharedEnterprise}
            disabled={disabled || isDisabled}
          />
        </Authorizer>
      )}
    </Stack>
  );
};
